import React, { useEffect, useState } from "react";
import { EntraSecurityAdminDto } from "../../../services/ApiTypes.ts";
import { Button, Form, Input, Typography, Radio, Row, Col, Modal } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import './EntraModal.css';

const { Text } = Typography;

interface EntraSecurityEntryProps {
  entraSecurityEntry: EntraSecurityAdminDto;
  isSelected: boolean;
  onSave: (entry: EntraSecurityAdminDto) => void;
  onDelete: (entry: EntraSecurityAdminDto) => void;
  onSelect: (entry: EntraSecurityAdminDto) => void;
}

const EntraSecurityEntry: React.FC<EntraSecurityEntryProps> = ({
  entraSecurityEntry,
  isSelected,
  onSave,
  onDelete,
  onSelect,
}) => {
  const [entry] = useState<EntraSecurityAdminDto>(entraSecurityEntry);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaveEnabled, setIsSaveEnabled] = useState<boolean>(entraSecurityEntry.id !== null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

  const [form] = Form.useForm();

  const startEdit = () => {
    setIsEditing(true);
    form.setFieldsValue(entry);
  };

  useEffect(() => {
    if (entraSecurityEntry.id === null) {
      setIsEditing(true);
    }
  }, [entraSecurityEntry]);

  const cancelEdit = () => {
    if (entraSecurityEntry.id === null) {
      onDelete(entraSecurityEntry);
    } else {
      setIsEditing(false);
    }
  };

  const saveEdit = async () => {
    try {
      const values = await form.validateFields();
      onSave({ ...entry, ...values });
      setIsEditing(false);
    } catch (errorInfo) {
      console.log("Validation Failed:", errorInfo);
    }
  };

  const showDeleteModal = () => {
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    setIsDeleteModalVisible(false);
    onDelete(entry);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
  };

  const handleRadioChange = () => {
    onSelect(entry);
  };

  const onInputChange = () => {
    form.validateFields().then(() => {
      setIsSaveEnabled(true);
    }).catch(() => {
      setIsSaveEnabled(false);
    });
  };

  return (
    <div>
      <Row align="middle" className="entra-entry-row">
        <Col span={2}>
          <Radio checked={isSelected} onChange={handleRadioChange} disabled={entry.id == null} />
        </Col>
        <Col span={16}>
          {isEditing ? (
            <Form form={form} className="entra-form" layout="horizontal" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} colon={false}>
              <Form.Item
                label={<span>Name</span>}
                name="name"
                rules={[{ required: true, message: "Name is required" }]}
              >
                <Input className="entra-input" onChange={onInputChange} />
              </Form.Item>
              <Form.Item
                label={<span>Tenant Id</span>}
                name="tenantId"
                rules={[
                  { required: true, message: "Tenant Id is required" },
                  {
                    pattern: /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    message: "Invalid GUID format",
                  },
                ]}
              >
                <Input className="entra-input" onChange={onInputChange} />
              </Form.Item>
              <Form.Item
                label={<span className="entra-form-label">Client Id</span>}
                name="clientId"
                rules={[
                  { required: true, message: "Client Id is required" },
                  {
                    pattern: /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    message: "Invalid GUID format",
                  },
                ]}
              >
                <Input className="entra-input" onChange={onInputChange} />
              </Form.Item>
            </Form>
          ) : (
            <div>
              <Text><strong>Name:</strong> {entry.name}</Text><br />
              <Text><strong>Tenant Id:</strong> {entry.tenantId}</Text><br />
              <Text><strong>Client Id:</strong> {entry.clientId}</Text><br />
            </div>
          )}
        </Col>
        <Col span={4} className="entra-buttons-column">
          {isEditing ? (
            <>
              <Button
                type="primary"
                onClick={saveEdit}
                disabled={!isSaveEnabled}
                className="entra-input-button"
                data-testid={"saveButton-" + entry.id}
              >
                Create
              </Button>
              <Button
                onClick={cancelEdit}
                className="entra-input-button"
                data-testid={"cancelButton" + entry.id}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={startEdit}
                data-testid={"editButton-" + entry.id}
              />
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
                onClick={showDeleteModal}
                data-testid={"deleteButton-" + entry.id}
              />
            </>
          )}
        </Col>
      </Row>

      <Modal
        title="Confirm Deletion"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this entry?</p>
      </Modal>
    </div>
  );
};

export default EntraSecurityEntry;
